import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ProductSelector from "../ProductSelector/ProductSelector";
import ModelSelector from "../ModelSelector/ModelSelector";
import BackgroundSelector from "../BackgroundSelector/BackgroundSelector";
import ImageProcess from "../ImageProcess/ImageProcess";
import { useDispatch } from "react-redux";
import { saveHeaderTitle } from "../../features/HomePage/HomePageSlice";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const MODEL_PAGE_LIMIT = 14;
const BACKGROUND_PAGE_LIMIT = 21;

const createPaginationState = (limit) => ({
  page: 1,
  limit,
  total: 0,
  totalPages: 1,
  search: "",
});

const getPayloadData = (payload) => payload?.data ?? payload ?? {};

const getPaginationMeta = (payload, fallbackLimit) => {
  const data = getPayloadData(payload);
  const limit = Number(data?.limit) || fallbackLimit;
  const total = Number(data?.total) || 0;
  const page = Number(data?.page) || 1;
  const totalPages = Math.max(1, Number(data?.totalPages) || Math.ceil(total / Math.max(1, limit)) || 1);

  return { page, limit, total, totalPages };
};

const buildPlaygroundUrl = (path, params) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  return `${endpoint.API_BASE_URL}${path}?${query.toString()}`;
};

const normalizeModelRows = (payload) => {
  const rows = Array.isArray(payload?.data?.rows)
    ? payload.data.rows
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload)
          ? payload
          : [];

  return rows.map((model) => {
    const activeFlag =
      model.bIsActive !== undefined
        ? Boolean(model.bIsActive && model.bIsActive !== "0" && model.bIsActive !== 0)
        : model.isActive !== undefined
          ? Boolean(model.isActive && model.isActive !== "0" && model.isActive !== 0)
          : model.status !== undefined
            ? Boolean(model.status === true || model.status === 1 || model.status === "1" || model.status === "active")
            : true;

    return {
      ...model,
      id: model.id ?? model.iModelId ?? model.modelId,
      front: model.front || model.images?.[0] || model.image || "",
      back: model.back || model.images?.[1] || "",
      bIsActive: activeFlag,
      isActive: activeFlag,
    };
  });
};

const normalizeBackgroundRows = (payload) => {
  const rows = Array.isArray(payload?.data?.rows)
    ? payload.data.rows
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload)
          ? payload
          : [];

  return rows.map((background) => {
    const activeFlag =
      background.bIsActive !== undefined
        ? Boolean(background.bIsActive && background.bIsActive !== "0" && background.bIsActive !== 0)
        : background.isActive !== undefined
          ? Boolean(background.isActive && background.isActive !== "0" && background.isActive !== 0)
          : background.status !== undefined
            ? Boolean(background.status === true || background.status === 1 || background.status === "1" || background.status === "active")
            : true;

    return {
      ...background,
      id: background.id ?? background.iBackgroundId ?? background.backgroundId,
      backgroundId: background.backgroundId ?? background.iBackgroundId ?? background.id,
      front: background.front || background.image || background.images?.[0] || "",
      image: background.image || background.front || background.images?.[0] || "",
      bIsActive: activeFlag,
      isActive: activeFlag,
    };
  });
};

const ModelBackgroundPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const token = useSelector((state) => state.auth?.token);
  const userRole = useSelector((state) => state.auth?.role);
  const isUserRole = userRole === "user" || userRole !== "admin";
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedTransformedImage, setSelectedTransformedImage] =
    useState(null);
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState(null);
  const [step, setStepState] = useState(1);
  const [selectionMode, setSelectionMode] = useState("user"); // "model" or "user"
  const maxStepVisitedRef = useRef(1);

  // Keep maxStepVisitedRef updated when step updates
  useEffect(() => {
    maxStepVisitedRef.current = Math.max(maxStepVisitedRef.current, step);
  }, [step]);

  // Reset maxStepVisited when selectionMode changes
  useEffect(() => {
    maxStepVisitedRef.current = step;
  }, [selectionMode]);

  const setStep = useCallback((targetStep) => {
    setStepState((currentStep) => {
      const resolvedTarget = typeof targetStep === "function" ? targetStep(currentStep) : targetStep;
      
      // If we are navigating forward
      if (resolvedTarget > currentStep) {
        const lastStep = selectionMode === "user" ? 3 : 4;
        const maxVisited = Math.min(maxStepVisitedRef.current, lastStep);
        if (maxVisited > resolvedTarget) {
          return maxVisited;
        }
      }
      return resolvedTarget;
    });
  }, [selectionMode]);

  const [generatedImage, setGeneratedImage] = useState({ front: "", back: "" });
  const [showMainProduct, setShowMainProduct] = useState(false);
  const [lastGeneratedParams, setLastGeneratedParams] = useState(null);

  // Reset to first step whenever navigating to this page (even if already on it)
  useEffect(() => {
    setStepState(1);
    maxStepVisitedRef.current = 1;
    setSelectedProduct(null);
    setSelectedModel(null);
    setSelectedBackgroundImage(null);
    setSelectedTransformedImage(null);
    setGeneratedImage({ front: "", back: "" });
    setShowMainProduct(false);
    setLastGeneratedParams(null);
  }, [location.key]);

  const [modelsData, setModelsData] = useState({});
  const [modelsLoading, setModelsLoading] = useState(false);
  const [backgroundsData, setBackgroundsData] = useState({});
  const [backgroundsLoading, setBackgroundsLoading] = useState(false);
  const [personsData, setPersonsData] = useState({});
  const [personsLoading, setPersonsLoading] = useState(false);

  const [modelPagination, setModelPagination] = useState({
    model: createPaginationState(MODEL_PAGE_LIMIT),
    user: createPaginationState(MODEL_PAGE_LIMIT),
  });
  const [backgroundPagination, setBackgroundPagination] = useState(createPaginationState(BACKGROUND_PAGE_LIMIT));

  const prevGenderRef = useRef();

  // Fetch models for current category
  const fetchModels = async (gender, type = "model", options = {}) => {
    if (!token) return;

    const paginationKey = type;
    const currentPagination = modelPagination[paginationKey] || createPaginationState(MODEL_PAGE_LIMIT);
    const page = options.page ?? currentPagination.page;
    const search = options.search ?? currentPagination.search;

    try {
      if (type === "user") {
        setPersonsLoading(true);
      } else {
        setModelsLoading(true);
      }

      const response = await apiHandler({
        url: buildPlaygroundUrl(endpoint.GET_MODELS, {
          gender,
          type,
          search,
          page,
          limit: MODEL_PAGE_LIMIT,
        }),
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      let models = normalizeModelRows(response?.data);
      if (isUserRole) {
        models = models.filter((m) => m.bIsActive);
      }
      const meta = getPaginationMeta(response?.data, MODEL_PAGE_LIMIT);

      setModelPagination((prev) => ({
        ...prev,
        [paginationKey]: {
          ...(prev[paginationKey] || createPaginationState(MODEL_PAGE_LIMIT)),
          ...meta,
          search,
          limit: MODEL_PAGE_LIMIT,
        },
      }));

      if (type === "user") {
        setPersonsData((prev) => ({
          ...prev,
          [gender]: models,
        }));
      } else {
        setModelsData((prev) => ({
          ...prev,
          [gender]: models,
        }));
      }
    } catch (err) {
      console.error("Error fetching models:", err);
      if (type === "user") {
        setPersonsData((prev) => ({
          ...prev,
          [gender]: [],
        }));
      } else {
        setModelsData((prev) => ({
          ...prev,
          [gender]: [],
        }));
      }
    } finally {
      if (type === "user") {
        setPersonsLoading(false);
      } else {
        setModelsLoading(false);
      }
    }
  };

  const fetchBackgrounds = async (gender, options = {}) => {
    if (!token) return;

    const page = options.page ?? backgroundPagination.page;
    const search = options.search ?? backgroundPagination.search;

    try {
      setBackgroundsLoading(true);
      const response = await apiHandler({
        url: buildPlaygroundUrl(endpoint.GET_BACKGROUNDS, {
          gender,
          search,
          page,
          limit: BACKGROUND_PAGE_LIMIT,
        }),
        method: "GET",
        authToken: { token },
        noCache: true,
      });

      let backgrounds = normalizeBackgroundRows(response?.data);
      if (isUserRole) {
        backgrounds = backgrounds.filter((bg) => bg.bIsActive);
      }
      const meta = getPaginationMeta(response?.data, BACKGROUND_PAGE_LIMIT);

      setBackgroundPagination((prev) => ({
        ...prev,
        ...meta,
        search,
        limit: BACKGROUND_PAGE_LIMIT,
      }));

      setBackgroundsData((prev) => ({
        ...prev,
        [gender]: backgrounds,
      }));
    } catch (err) {
      console.error("Error fetching backgrounds:", err);
      setBackgroundsData((prev) => ({
        ...prev,
        [gender]: [],
      }));
    } finally {
      setBackgroundsLoading(false);
    }
  };

  const fetchPersons = async (gender, options = {}) => {
    return fetchModels(gender, "user", options);
  };

  // Refresh models for current product category and prefetch updated lists
  const handleRefreshModels = () => {
    if (!token) return;

    fetchModels("women", "model");
    fetchModels("men", "model");
    fetchPersons("women");
    fetchPersons("men");
  };

  // Prefetch models, persons, and backgrounds for both genders on mount (Step 1)
  useEffect(() => {
    if (!token) return;
    fetchModels("women", "model");
    fetchModels("men", "model");
    fetchBackgrounds("women");
    fetchBackgrounds("men");
    fetchPersons("women");
    fetchPersons("men");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!selectedProduct || !token) return;

    const gender = selectedProduct.category === "men" ? "men" : "women";
    const isDefaultPagination = modelPagination.model.page === 1 && !modelPagination.model.search;
    if (!modelsData[gender] || modelsData[gender].length === 0 || !isDefaultPagination) {
      fetchModels(gender, "model");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.category, token, modelPagination.model.page, modelPagination.model.search]);

  useEffect(() => {
    if (!selectedProduct || !token) return;

    const gender = selectedProduct.category === "men" ? "men" : "women";
    const isDefaultPagination = modelPagination.user.page === 1 && !modelPagination.user.search;
    if (!personsData[gender] || personsData[gender].length === 0 || !isDefaultPagination) {
      fetchPersons(gender);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.category, token, modelPagination.user.page, modelPagination.user.search]);

  // Fetch backgrounds for current category
  useEffect(() => {
    if (!selectedProduct || !token) return;

    const gender = selectedProduct.category === "men" ? "men" : "women";
    const isDefaultPagination = backgroundPagination.page === 1 && !backgroundPagination.search;
    if (!backgroundsData[gender] || backgroundsData[gender].length === 0 || !isDefaultPagination) {
      fetchBackgrounds(gender);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct?.category, token, backgroundPagination.page, backgroundPagination.search]);

  const handleRefreshBackgrounds = () => {
    if (!selectedProduct || !token) return;

    const gender = selectedProduct.category === "men" ? "men" : "women";
    fetchBackgrounds(gender);
  };

  useEffect(() => {
    if (selectionMode === "user") {
      setSelectedBackgroundImage(null);
      if (selectedProduct) {
        const currentGender =
          selectedProduct.category === "men" ? "men" : "women";
        const persons = personsData[currentGender] || [];
        setSelectedModel(persons[0]);
      }
    } else if (selectionMode === "model" && !selectedBackgroundImage && selectedProduct) {
      const currentGender =
        selectedProduct.category === "men" ? "men" : "women";
      const backgrounds =
        backgroundsData[currentGender] || [];
      setSelectedBackgroundImage(backgrounds[0]);
      const models = modelsData[currentGender] || [];
      setSelectedModel(models[0]);
    }
  }, [selectionMode, selectedProduct, modelsData, personsData]);

  useEffect(() => {
    if (selectedProduct) {
      const currentGender =
        selectedProduct.category === "men" ? "men" : "women";
      const models = modelsData[currentGender] || [];
      const backgrounds =
        backgroundsData[currentGender] || [];

      if (prevGenderRef.current && prevGenderRef.current !== currentGender) {
        // Gender changed: Force selection of the first item
        if (selectionMode === "model") {
          setSelectedModel(models[0]);
          setSelectedBackgroundImage(backgrounds[0]);
        } else {
          const persons = personsData[currentGender] || [];
          setSelectedModel(persons[0]);
          setSelectedBackgroundImage(null);
        }
      } else if (!prevGenderRef.current) {
        if (!selectedModel) {
          if (selectionMode === "user") {
            const persons = personsData[currentGender] || [];
            setSelectedModel(persons[0]);
          } else {
            setSelectedModel(models[0]);
          }
        }
        if (selectionMode === "model" && !selectedBackgroundImage) {
          setSelectedBackgroundImage(backgrounds[0]);
        } else if (selectionMode === "user") {
          setSelectedBackgroundImage(null);
        }
      }
      prevGenderRef.current = currentGender;
    }
  }, [selectedProduct, selectedModel, selectedBackgroundImage, modelsData, backgroundsData, personsData, selectionMode]);

  const handleModelSearchChange = (search) => {
    setModelPagination((prev) => ({
      ...prev,
      [selectionMode]: {
        ...(prev[selectionMode] || createPaginationState(MODEL_PAGE_LIMIT)),
        page: 1,
        search,
      },
    }));
  };

  const handleModelPageChange = (page) => {
    setModelPagination((prev) => ({
      ...prev,
      [selectionMode]: {
        ...(prev[selectionMode] || createPaginationState(MODEL_PAGE_LIMIT)),
        page,
      },
    }));
  };

  const handleBackgroundSearchChange = (search) => {
    setBackgroundPagination((prev) => ({
      ...prev,
      page: 1,
      search,
    }));
  };

  const handleBackgroundPageChange = (page) => {
    setBackgroundPagination((prev) => ({
      ...prev,
      page,
    }));
  };

  useEffect(() => {
    let title = "Select Product";
    switch (step) {
      case 1:
        title = "Select Product";
        break;
      case 2:
        title = "Select Model";
        break;
      case 3:
        title = selectionMode === "user" ? "Processing Image" : "Select Background";
        break;
      case 4:
        title = "Processing Image";
        break;
      default:
        title = "Select Product";
    }
    dispatch(saveHeaderTitle(title));
  }, [step, dispatch, selectionMode]);

  return (
    <>
      {step === 1 && (
        <ProductSelector
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          setStep={setStep}
          modelsData={modelsData}
          backgroundsData={backgroundsData}
        />
      )}
      {step === 2 && (
        <ModelSelector
          selectedProduct={selectedProduct}
          setSelectedModel={setSelectedModel}
          selectedModel={selectedModel}
          setStep={setStep}
          step={step}
          selectionMode={selectionMode}
          setSelectionMode={setSelectionMode}
          modelsData={modelsData}
          personsData={personsData}
          onRefreshModels={handleRefreshModels}
          isLoading={
            selectionMode === "user"
              ? personsLoading
              : modelsLoading
          }
          pagination={modelPagination[selectionMode] || createPaginationState(MODEL_PAGE_LIMIT)}
          onSearchCommit={handleModelSearchChange}
          onPageChange={handleModelPageChange}
        />
      )}
      {step === 3 && selectionMode !== "user" && (
        <BackgroundSelector
          selectedProduct={selectedProduct}
          selectedModel={selectedModel}
          selectedBackgroundImage={selectedBackgroundImage}
          setSelectedBackgroundImage={setSelectedBackgroundImage}
          setStep={setStep}
          step={step}
          backgroundsData={backgroundsData}
          onRefreshBackgrounds={handleRefreshBackgrounds}
          isLoading={backgroundsLoading}
          pagination={backgroundPagination}
          onSearchCommit={handleBackgroundSearchChange}
          onPageChange={handleBackgroundPageChange}
        />
      )}
      {step === (selectionMode === "user" ? 3 : 4) && (
        <ImageProcess
          selectedProduct={selectedProduct}
          selectedModel={selectedModel}
          selectedBackgroundImage={selectedBackgroundImage}
          setStep={setStep}
          step={step}
          generatedImage={generatedImage}
          setGeneratedImage={setGeneratedImage}
          showMainProduct={showMainProduct}
          setShowMainProduct={setShowMainProduct}
          lastGeneratedParams={lastGeneratedParams}
          setLastGeneratedParams={setLastGeneratedParams}
          selectionMode={selectionMode}
        />
      )}
    </>
  );
};

export default ModelBackgroundPage;
